/**
 * EmptyState primitive (Plan 06-01, Task 1).
 *
 * Standardised empty-state used by every feature surface (chore pool,
 * approvals, calendar, shopping, points, rewards, achievements,
 * leaderboard, assistant conversations, admin push-health). Replaces
 * the ad-hoc `data?.length === 0 && null` / `<p>Ingen ...</p>` patterns
 * that landed across Phases 1-5 — see 06-UI-SPEC.md D-04 +
 * §"Retroactive application rule".
 *
 * Variants:
 *   inline (default) — heading 17/600; body 14/400 text-ink-2;
 *                       max-width
 *                       constrains the body to readable line-length.
 *   page              — Full-viewport empty; heading Display S 28/600.
 *
 * Slots:
 *   illustration — optional inline SVG rendered above the heading.
 *                   Feature SVGs live alongside the feature (06-UI-SPEC
 *                   §D-15).
 *   cta          — optional primary action ("Legg til oppgave",
 *                   "Be om en belønning" etc.).
 *
 * ARIA:
 *   - root `role="region"` with `aria-label={heading}` so the empty
 *     state announces itself as a named landmark when focused / read.
 *
 * Norwegian copy: heading/body/cta-label come from feature i18n
 * namespaces declared in 06-01 Task 4.
 */
import type { JSX, ReactNode } from 'react';
import { Card } from './Card';
import { Button } from './Button';

export interface EmptyStateProps {
  variant?: 'inline' | 'page';
  heading: string;
  body?: string;
  cta?: { label: string; onClick: () => void };
  illustration?: ReactNode;
}

export function EmptyState({
  variant = 'inline',
  heading,
  body,
  cta,
  illustration,
}: EmptyStateProps): JSX.Element {
  if (variant === 'page') {
    return (
      <div
        role="region"
        aria-label={heading}
        data-testid="empty-state"
        className="flex flex-col items-center justify-center gap-4 min-h-vh-60 px-6 text-center"
      >
        {illustration ? <div data-testid="empty-illustration">{illustration}</div> : null}
        <h1 className="text-display-s font-extrabold tracking-tight text-ink">
          {heading}
        </h1>
        {body ? <p className="text-sm text-ink-2 max-w-80">{body}</p> : null}
        {cta ? (
          <Button
            variant="primary"
            size={52}
            aria-label={cta.label}
            onClick={cta.onClick}
          >
            {cta.label}
          </Button>
        ) : null}
      </div>
    );
  }

  // inline
  return (
    <Card variant="soft" className="!p-0">
      <div
        role="region"
        aria-label={heading}
        data-testid="empty-state"
        className="flex flex-col items-center justify-center gap-3 py-8 px-4"
      >
        {illustration ? <div data-testid="empty-illustration">{illustration}</div> : null}
        <h2 className="text-heading-s font-extrabold text-ink">{heading}</h2>
        {body ? (
          <p className="text-sm text-ink-2 max-w-70 text-center">{body}</p>
        ) : null}
        {cta ? (
          <Button
            variant="primary"
            aria-label={cta.label}
            onClick={cta.onClick}
          >
            {cta.label}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
